import {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { timecodeAt } from "@infoflyt/shared/timecode";
import { useExitDelay } from "./components/useExitDelay";
import { Pager } from "./components/Pager";
import { useClockTick, useWakeLock } from "./clock/useClockTick";
import { SessionProvider, useSession, useVisiblePages } from "./state/SessionProvider";
import { InnloggingScreen } from "./screens/InnloggingScreen";
import { innlogget, oppdaterOekt } from "./state/session";
import { krevInnlogging, supabase } from "./state/supabase";
import {
  DOKUMENTSIDER, erDokumentsidenavn, feltId, sammeNavn, sidenavn,
  type PageId, type SideId,
} from "./state/types";
import { foelgViewport } from "./state/kbd";
import { LoggScreen } from "./screens/LoggScreen";
import { HurtigloggScreen } from "./screens/HurtigloggScreen";
import { TimerScreen } from "./screens/TimerScreen";
import {
  DocumentScreen, type DocumentState, type TidligereDokument,
} from "./screens/DocumentScreen";
import { DokumentFlate } from "./screens/DokumentFlate";
import { lesLokalt, synkroniser, type LokaltDokument } from "./state/dokumenter";
import { markerLest } from "./state/db";
import * as su from "./state/su";
import { SuScreen } from "./screens/SuScreen";
import * as tekstflater from "./state/tekstflater";
import * as loggsynk from "./state/loggsynk";
import * as rapportsynk from "./state/rapportsynk";
import * as tilbakemelding from "./state/tilbakemelding";
import { proevIgjen, startLevende } from "./state/levende";
import * as shotlist from "./state/shotlist";
import { BriefScreen } from "./screens/BriefScreen";
import { ShotlistScreen } from "./screens/ShotlistScreen";
import type { Tekstflate } from "./state/types";
import { erTom as erTomTekst, type Tekstdok } from "@infoflyt/shared/tekst";
import { TimecodeSheet } from "./screens/TimecodeSheet";
import { TopBar } from "./screens/TopBar";
import { jamInfo } from "./components/jamTekst";
import { Menu, type MenuHandle } from "./screens/menu/Menu";
import { Invitasjon } from "./components/Invitasjon";


/*
 * Slaget egendefinerte felter har. Prodleder lager dem paa nettflata med
 * «Legg til felt», og de blir sider her som alle andre.
 */
const EGET_SLAG = "annet";

/**
 * Titlene som fortjener en side for seg.
 *
 * Et egendefinert felt som heter det samme som en fast dokumentside blir
 * ikke en side til — det er den faste. Se `erDokumentsidenavn`.
 */
function egneFelter(dokumenter: readonly { slag: string; tittel: string }[]): string[] {
  return [...new Set(
    dokumenter
      .filter((d) => d.slag === EGET_SLAG && !erDokumentsidenavn(d.tittel))
      .map((d) => d.tittel),
  )];
}

/** ISO-tidspunkt → «v2 · lastet 06:40», slik DocumentScreen venter det. */
function metaAv(versjon: number, lastetOpp: string): string {
  const d = new Date(lastetOpp);
  const klokke = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `v${versjon} · lastet ${klokke}`;
}

function Shell(): JSX.Element {
  const session = useSession();
  const {
    anchor, spec, showTc, blackout, uncertainSince, handleResume, sampleClock,
    registrerFelter,
  } = session;
  const valgteSider = useVisiblePages();

  /*
   * Radene til nedtrekket, memoisert.
   *
   * TopBar gjengis ved hvert klokketikk — ti ganger i sekundet — og en ny
   * array hver gang ville gjort `ProduksjonVelger` til en ny gjengiving
   * uansett hva den inneholder.
   */
  const velgerader = useMemo(
    () => session.produksjoner.map((p) => ({ id: p.production.id, navn: p.production.name })),
    [session.produksjoner],
  );

  const [index, setIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  /*
   * Hva menyknappen skal vise.
   *
   * Skilt fra `menuOpen`, som lever til utgangsanimasjonen er ferdig.
   * Ikonet skal snu i trykkøyeblikket.
   */
  const [menuShown, setMenuShown] = useState(false);
  const menuRef = useRef<MenuHandle | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  /* Hva som staar maksimert. Enten dagens dokument paa en side, eller en
     tidligere opptaksdag fra lista under. Begge ender i samme viser. */
  const [visning, setVisning] = useState<Visning | null>(null);

  /*
   * APPEN FAAR BESKJED, OG SPOER NESTEN ALDRI.
   *
   * Flatene oppdaterte seg foer bare naar appen ble lagt bort og tatt fram
   * igjen. Saa spurte den hvert 30. sekund i stedet — og hentet ned
   * dokumentlista, beskjedene, shotlista og redaktoerens notater hundre og
   * tjue ganger i timen for aa finne ut at ingenting var nytt.
   *
   * Naa lytter den. Se state/levende.ts for hvorfor en aapen kanal er
   * BILLIGERE enn den takten, og ikke dyrere: det er den unoedvendige
   * hentingen som koster, ikke forbindelsen.
   *
   * KANALEN LIGGER IKKE I LOMMA. Den aapnes naar skjermen viser appen og
   * lukkes naar den gaar i moerke.
   *
   * OG TAKTEN STAAR, BARE TREG. Realtime sender ENDRINGER: ligger
   * forbindelsen nede, vet man ikke hva man gikk glipp av. Fem minutter er
   * et nett under, ikke en hentemaate — og `levende` varsler ogsaa naar
   * kanalen kommer opp igjen, saa det vanlige gjensynet er dekket der.
   *
   * Et vindusbudskap og ikke et kall: hver flate henter sitt eget, og
   * verken takten eller kanalen vet hva de henter. Samme moenster som
   * `infoflyt:loggkoe`.
   */
  useEffect(() => {
    const pid = session.production.id;
    let stopp: (() => void) | null = null;

    const slag = (): void => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      window.dispatchEvent(new CustomEvent("infoflyt:oppdater"));
    };

    const stem = (): void => {
      const skalVaereAapen = document.visibilityState === "visible";
      if (skalVaereAapen && stopp === null) {
        stopp = startLevende(pid);
      } else if (!skalVaereAapen && stopp !== null) {
        stopp();
        stopp = null;
      }
    };

    stem();
    const naarSynlig = (): void => { stem(); slag(); };
    /* Fem minutter: nettet under kanalen, ikke hentemaaten. */
    const id = window.setInterval(slag, 5 * 60 * 1000);
    window.addEventListener("online", slag);
    document.addEventListener("visibilitychange", naarSynlig);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("online", slag);
      document.removeEventListener("visibilitychange", naarSynlig);
      if (stopp !== null) stopp();
    };
  }, [session.production.id]);

  /*
   * Dokumentene for opptaksdagen. LOKALT FOERST.
   *
   * Lesing fra disk skjer uten aa roere nettet og svarer med én gang — en
   * regissoer i kjeller eller skog skal ha dagsplanen sin uten aa vente paa
   * noe. Synkroniseringen kjoerer etterpaa og henter bare det som faktisk er
   * nytt; finner den noe, leses det lokale inn paa nytt.
   *
   * Henter paa nytt naar takten slaar. Se effekten over for hvorfor det er
   * polling og ikke Realtime, og hvorfor det bare skjer mens skjermen viser
   * appen.
   */
  const [dokumenter, setDokumenter] = useState<readonly LokaltDokument[]>([]);

  useEffect(() => {
    let avbrutt = false;
    let lagde: string[] = [];

    const tegn = (lokale: readonly LokaltDokument[]): void => {
      /*
       * De tre faste sidene OG feltene prodleder har laget.
       *
       * Her laa hullet: filteret slapp bare gjennom de tre, saa «Legg til
       * felt» paa nettflata lagde en side som aldri naadde telefonen.
       */
      const mine = lokale.filter(
        (d) => (DOKUMENTSIDER as readonly string[]).includes(d.slag) || d.slag === EGET_SLAG,
      );
      for (const d of mine) if (d.url !== null) lagde.push(d.url);
      setDokumenter(mine);
    };

    const paaTakt = (): void => { void hentNytt(); };
    window.addEventListener("infoflyt:oppdater", paaTakt);

    void (async () => {
      /*
       * Feiler dette, staar flata uten dokumenter — den faller ikke.
       * Loggingen er kjernen i appen, og en dagsplan som ikke lot seg hente
       * skal aldri kunne hindre at regi faar skrevet.
       */
      let lokale: Awaited<ReturnType<typeof lesLokalt>>;
      try {
        lokale = await lesLokalt(session.production.id);
      } catch {
        return;
      }
      if (avbrutt) return;
      tegn(lokale);

      await hentNytt();
    })();

    return () => {
      avbrutt = true;
      window.removeEventListener("infoflyt:oppdater", paaTakt);
      // Blob-URL-ene holder fila i minnet til de slippes.
      for (const u of lagde) URL.revokeObjectURL(u);
    };

    /*
     * Henter ned det som er nytt, og tegner bare naar noe FAKTISK kom.
     *
     * `oppdaterte.length === 0` er vakta mot aa bygge blob-URL-ene paa nytt
     * hvert halve minutt: uten den ville hver takt revet ned dokumentet
     * regi staar og leser i.
     */
    async function hentNytt(): Promise<void> {
      let svar: Awaited<ReturnType<typeof synkroniser>>;
      try {
        svar = await synkroniser(session.production.id);
      } catch {
        /* Varselet er brukt opp. Uten dette sto dokumentet og ventet paa
           den trege takten. Se `proevIgjen` i state/levende.ts. */
        proevIgjen();
        return;
      }
      /* `utenNett` er hentingens maate aa si at den ikke kom fram paa. */
      if (svar.utenNett) proevIgjen();
      if (avbrutt || svar.oppdaterte.length === 0) return;

      // Noe nytt kom ned. Slipp de gamle blob-URL-ene foer de nye lages.
      for (const u of lagde) URL.revokeObjectURL(u);
      lagde = [];
      const paaNytt = await lesLokalt(session.production.id);
      if (!avbrutt) tegn(paaNytt);
    }
  }, [session.production.id, session.shootDay]);

  /*
   * Feltene meldes inn som sider — MEN FOERST NAAR OEKTEN ER LASTET.
   *
   * Her laa en feil: innmeldingen hang paa dokumenteffekten, som ofte er
   * ferdig foer `loadPagePreferences`. Da saa den en sideliste uten de
   * lagrede radene, la feltet til paa nytt som paaslaatt, og SKREV det over
   * valget brukeren hadde gjort. Et felt man hadde skrudd av var tilbake ved
   * neste oppstart.
   *
   * `ready` settes etter at sidevalgene er lest inn, saa her er `prev` det
   * som faktisk staar lagret. Nye felt kommer bakerst og paaslaatt — ingen
   * rad betyr synlig — og kjente felt roeres ikke.
   */
  /*
   * Det redaktoeren har lagt igjen paa postene.
   *
   * LOKALT FOERST, SAA HENTING — samme moenster som beskjedene og shotlista.
   * Her laa feilen: bare den lokale lesingen fantes, og hentingen laa i
   * «frisk», som bare kjoerer paa `online` og naar fana blir synlig igjen.
   * Starter man appen og blir staaende i den, kom beskjeden fra redaktoer
   * aldri fram — man maatte legge telefonen bort og ta den opp igjen.
   */
  useEffect(() => {
    if (!session.ready) return;
    let avbrutt = false;
    const pid = session.production.id;
    void (async () => {
      try {
        const lokalt = await tilbakemelding.lesLokalt(pid);
        if (!avbrutt) setMerknader(lokalt);
      } catch {
        return;
      }
      try {
        const friskt = await tilbakemelding.synkroniser(pid);
        if (!avbrutt && friskt !== null) setMerknader(friskt);
      } catch { /* det lokale staar */ }
    })();

    /*
     * OG MENS APPEN STAAR AAPEN.
     *
     * Redaktoeren skriver mens opptaket gaar — «noe er oversett, foelg
     * dette» — og da er «neste gang telefonen tas opp av lomma» for sent.
     * Henger paa den felles takten, saa det finnes ett sted som bestemmer
     * hvor ofte appen spoer.
     */
    const paaTakt = (): void => {
      void (async () => {
        try {
          const friskt = await tilbakemelding.synkroniser(pid);
          if (friskt === null) proevIgjen();
          else if (!avbrutt) setMerknader(friskt);
        } catch { proevIgjen(); }
      })();
    };
    window.addEventListener("infoflyt:oppdater", paaTakt);

    return () => {
      avbrutt = true;
      window.removeEventListener("infoflyt:oppdater", paaTakt);
    };
  }, [session.ready, session.production.id]);

  useEffect(() => {
    if (!session.ready) return;
    registrerFelter(egneFelter(dokumenter));
  }, [session.ready, dokumenter, registrerFelter]);
  /*
   * Deltakere og SU-/synkspoersmaal.
   *
   * Lastes her og ikke i SessionProvider, av samme grunn som dokumentene:
   * det er innhold som hoerer til en flate, ikke tilstand loggen trenger.
   * Lokalt foerst, saa synk — en synk tas sjelden der det er dekning.
   */
  const [suData, setSuData] = useState<su.SuData>(su.TOM_SU);

  useEffect(() => {
    let avbrutt = false;
    void (async () => {
      try {
        const lokalt = await su.lesLokalt(session.production.id);
        if (!avbrutt) setSuData(lokalt);
      } catch {
        // Flata staar tom. Loggingen er kjernen og skal aldri kunne falle
        // fordi en spoersmaalsliste ikke lot seg hente.
        return;
      }
      try {
        const friskt = await su.synkroniser(session.production.id);
        if (!avbrutt && friskt !== null) setSuData(friskt);
      } catch { /* det lokale staar */ }
    })();
    return () => { avbrutt = true; };
  }, [session.production.id]);

  /*
   * Tekstflatene. Brief for teknisk crew er den foerste.
   *
   * Lokalt foerst, saa synk — samme moenster som dokumentene og
   * spoersmaalene. En brief skal staa i en kjeller.
   */
  const [flater, setFlater] = useState<readonly Tekstflate[]>([]);
  /* Kommentarer, markeringer og synkspoersmaal fra redaktoer. Leses lokalt
     foerst, som alt annet — de skal staa paa posten uten dekning ogsaa. */
  const [merknader, setMerknader] = useState<readonly tilbakemelding.Merknad[]>([]);

  useEffect(() => {
    let avbrutt = false;
    void (async () => {
      try {
        const lokalt = await tekstflater.lesLokalt(session.production.id);
        if (!avbrutt) setFlater(lokalt);
      } catch {
        return;
      }
      try {
        // Toem utboksen FOER synken: ellers henter vi ned noe vi selv er i
        // ferd med aa sende, og brukerens avhuking ville sett ut som den
        // forsvant et oeyeblikk.
        await tekstflater.send(session.production.id);
        const friskt = await tekstflater.synkroniser(session.production.id);
        if (!avbrutt && friskt !== null) setFlater(friskt);
      } catch { /* det lokale staar */ }
    })();
    return () => { avbrutt = true; };
  }, [session.production.id]);

  /**
   * En endring fra appen: huket av, eller et nytt huskepunkt.
   *
   * Lokalt foerst — flata skal svare med én gang, ogsaa uten dekning — og
   * saa et forsoek paa aa sende. Lykkes det ikke, staar sida i utboksen til
   * neste gang appen aapnes eller nettet kommer tilbake.
   */
  const endreFlate = useCallback((slug: string, dok: Tekstdok): void => {
    const pid = session.production.id;
    setFlater((prev) => prev.map((f) => (f.slug === slug ? { ...f, dok } : f)));
    void (async () => {
      try {
        await tekstflater.skriv(pid, slug, dok);
        await tekstflater.send(pid);
      } catch {
        /*
         * Skrivingen staar lokalt uansett, og utboksen tar den neste gang.
         * Feiler den LOKALE skrivingen, kaster `skriv` — se db.ts — og da
         * er dette stedet som fanger det.
         */
      }
    })();
  }, [session.production.id]);

  /*
   * Shotlista. Egne tabeller, ikke en tekstflate — et bilde kan ha en
   * kommentar og et referansebilde, og det er data per linje.
   *
   * Samme moenster som over: lokalt foerst, saa synk. Per OPPTAKSDAG:
   * scenene er dagens settinger, og lista skal vaere fersk om morgenen.
   */
  const [shotliste, setShotliste] = useState<shotlist.Shotliste>(shotlist.TOM);
  /* Bumpes naar nye referansebilder har kommet ned, saa flata ser etter dem
     paa nytt. Uten den sto bildene usett til neste oppstart. */
  const [bildeklar, setBildeklar] = useState(0);
  const opptaksdag = session.shootDay;

  useEffect(() => {
    let avbrutt = false;
    void (async () => {
      try {
        const lokalt = await shotlist.lesLokalt(session.production.id, opptaksdag);
        if (!avbrutt) setShotliste(lokalt);
      } catch {
        return;
      }
      try {
        await shotlist.send(session.production.id);
        const friskt = await shotlist.synkroniser(session.production.id, opptaksdag);
        if (avbrutt || friskt === null) return;
        setShotliste(friskt);
        const nye = await shotlist.hentBilder(friskt.shots);
        if (!avbrutt && nye > 0) setBildeklar((n) => n + 1);
      } catch { /* det lokale staar */ }
    })();
    return () => { avbrutt = true; };
  }, [session.production.id, opptaksdag]);

  const hukShot = useCallback((shotId: string, gjort: boolean): void => {
    const pid = session.production.id;
    setShotliste((p) => ({
      ...p, shots: p.shots.map((b) => (b.id === shotId ? { ...b, gjort } : b)),
    }));
    void (async () => {
      try {
        await shotlist.settGjort(pid, shotId, gjort);
        await shotlist.send(pid);
      } catch { /* haken staar lokalt; utboksen tar den */ }
    })();
  }, [session.production.id]);

  /*
   * FLATENE HOLDER SEG SELV OPPDATERT.
   *
   * Naar takten slaar: toem utboksene og hent paa nytt. Ikke en aapen
   * forbindelse — en websocket som ligger og venter i en jakkelomme er feil
   * pris for noe som leses i rykk og napp, og appen skal uansett kunne staa
   * helt uten nett. Takten staar stille naar skjermen er moerk.
   *
   * Utboksen toemmes FOER hentingen: ellers henter vi ned noe vi selv er i
   * ferd med aa sende, og en avhuking ville sett ut som den forsvant.
   *
   * Merknadene fra redaktoer hentes IKKE her. De har sin egen effekt paa
   * den samme takten — laa de begge steder, ble hver beskjed hentet to
   * ganger per slag.
   */
  /*
   * LOGGEN GAAR OPP MENS DEN SKRIVES.
   *
   * Redaktoeren foelger loggene mens opptaket gaar, og da er «neste gang
   * telefonen tas opp av lomma» for sjelden. Derfor en egen, kort takt —
   * og bare naar skjermen er framme og nettet er der. `send` koster ett
   * lokalt oppslag naar koen er tom.
   *
   * LIVE ER BEST EFFORT. Staar regi uten dekning, blir koen staaende og
   * gaar opp naar linja kommer tilbake. Flata hos redaktoer viser hvor
   * gammelt bildet er framfor aa late som det er à jour.
   */
  useEffect(() => {
    const toem = (): void => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      /*
       * LOGGEN FOERST, SAA RAPPORTEN, OG ETTER HVERANDRE.
       *
       * Rapporten peker paa oekten, og oekten kommer opp med loggkoen. Gikk
       * de to samtidig, kunne rapporten tape kappleopet mot sin egen oekt —
       * og en rapport som ble avvist stoppet hele rapportkoen. Rekkefoelgen
       * koster ingenting naar begge koene er tomme.
       */
      void loggsynk.send(session)
        .catch(() => { /* koen staar */ })
        /*
         * OG SAA NED IGJEN: postene teamet har skrevet i den samme oekta.
         *
         * Etter sendingen, ikke foer. Vaare egne poster skal vaere oppe
         * foer vi henter — ellers kan en post vi nettopp skrev komme ned
         * igjen som «en annens» i det sekundet forfatteren ikke er oss.
         * (Den er filtrert paa `author_id`, saa det kan den ikke — men
         * rekkefoelgen koster ingenting, og den er den aarsakene staar i.)
         */
        .then(async () => {
          if (session.oektId === null) return;
          const nye = await loggsynk.hentFraTeamet(session, session.oektId);
          if (nye > 0) window.dispatchEvent(new Event("infoflyt:teamlogg"));
        })
        .catch(() => { /* uten nett staar vaart eget */ })
        .then(() => rapportsynk.send({
          produksjonId: session.production.id,
        }))
        .catch(() => { /* koen staar */ });
    };
    toem();
    const id = window.setInterval(toem, 4000);
    window.addEventListener("online", toem);
    /* Noe ble lagt i koen. Da gaar det opp naa, ikke ved neste takt. */
    window.addEventListener("infoflyt:loggkoe", toem);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("online", toem);
      window.removeEventListener("infoflyt:loggkoe", toem);
    };
  }, [session]);

  useEffect(() => {
    const pid = session.production.id;
    const dag = opptaksdag;
    const frisk = (): void => {
      void (async () => {
        try {
          await tekstflater.send(pid);
          const f = await tekstflater.synkroniser(pid);
          /* `null` ER feilen her, ikke et unntak. Se `proevIgjen`. */
          if (f === null) proevIgjen(); else setFlater(f);
        } catch { proevIgjen(); }
        try {
          await shotlist.send(pid);
          const sl = await shotlist.synkroniser(pid, dag);
          if (sl === null) { proevIgjen(); return; }
          setShotliste(sl);
          const nye = await shotlist.hentBilder(sl.shots);
          if (nye > 0) setBildeklar((n) => n + 1);
        } catch { proevIgjen(); }
      })();
    };
    window.addEventListener("infoflyt:oppdater", frisk);
    return () => { window.removeEventListener("infoflyt:oppdater", frisk); };
  }, [session.production.id, opptaksdag]);

  /* Lest i tilbakekallet under, som ikke skal bygges paa nytt for hver rad. */
  const suDataRef = useRef(suData);
  suDataRef.current = suData;

  const nyttSpoersmaal = useCallback((
    deltakerId: string | null, tekst: string, kildePostId: string | null = null,
  ): void => {
    const produksjonId = session.production.id;
    void (async () => {
      const q = await su.leggTilSpoersmaal(
        produksjonId, deltakerId, tekst,
        // Bakerst i sin egen gruppe. Et spoersmaal som dukker opp i felt skal
        // ikke dytte om paa rekkefoelgen redaktoer har satt.
        suDataRef.current.spoersmaal.filter((x) => x.deltakerId === deltakerId).length,
        opptaksdag,
        kildePostId,
      );
      setSuData((prev) => ({ ...prev, spoersmaal: [...prev.spoersmaal, q] }));
      /* Koblingen til posten kommer ned som en merknad ved neste henting —
         samme vei som redaktoerens. Se state/tilbakemelding.ts. */
      if (kildePostId !== null) {
        void tilbakemelding.synkroniser(produksjonId).then((m) => {
          if (m !== null) setMerknader(m);
        });
      }
    })();
  }, [session.production.id, opptaksdag]);

  const [editMode, setEditMode] = useState(false);
  const [scrollFrames, setScrollFrames] = useState<number | null>(null);
  const [frozenFrames, setFrozenFrames] = useState<number | null>(null);

  const now = useClockTick(true, handleResume);

  // Skjermen må stå på: en avlivet sideøkt nullstiller den monotone
  // tellingen, og låsen er derfor en del av tidskodens holdbarhet.
  useWakeLock(true);

  /*
   * Flatas hoeyde foelger den visuelle viewporten.
   *
   * `interactive-widget=resizes-content` staar i viewport-metaen, men iOS-en i
   * felt overser den: layout-viewporten beholder full hoeyde naar tastaturet
   * kommer. Flata er `position: fixed`, altsaa festet til nettopp den, saa uten
   * dette maalepunktet strekker den seg bak tastaturet — og loggposten man
   * skriver havner under det.
   *
   * Bare hoeyden leses. `offsetTop` er med vilje ikke med: iOS rapporterer
   * mellomverdier mens tastaturet glir opp, og aa forskyve flata etter dem
   * kastet toppstripa midt ut paa skjermen. Uten transform kan flata ikke
   * flytte seg — den kan bare bli lavere, nedenfra. Logikken ligger i
   * state/kbd.ts, som er det ene stedet `--vvh` skrives.
   */
  useEffect(foelgViewport, []);

  /*
   * Overleggene starter der toppstripa slutter.
   *
   * Høyden varierer med hakket på telefonen, så den måles i stedet for å
   * settes som et fast tall. Toppstripa blir stående over menyen — regi skal
   * aldri miste klokka.
   */
  useEffect(() => {
    // Avhenger av `ready`: før den er sann rendres bare en tom flate, og
    // toppstripa finnes ikke å måle. Uten avhengigheten ble verdien stående
    // på reserven for alltid.
    if (!session.ready) return;
    // Feltets overkant flytter seg når toppstripa eller satt-linja bytter
    // høyde, så begge overvåkes.
    const bar = document.querySelector(".app > .topbar");
    const satt = bar?.nextElementSibling;
    const set = (): void => {
      /*
       * Flata ligger fast i toppen uten transform, saa en boks paa skjermen
       * er entydig igjen. Det er feltet overlegget skal dekke, saa det er
       * feltet som bestemmer kanten.
       */
      const felt = document.querySelector(".pager");
      const top = felt instanceof HTMLElement
        ? felt.getBoundingClientRect().top
        : 117;
      /*
       * Rundes OPP, ikke av. Kanten er sjelden et helt tall, og en avrunding
       * nedover lar overlegget starte en brøkdel av en piksel for hoeyt — da
       * legger menyens egen bakgrunn seg over nederste kant av toppstripa og
       * tegner en lysere strek der.
       */
      document.documentElement.style.setProperty("--overlay-top", `${Math.ceil(top)}px`);
    };
    set();
    const ro = new ResizeObserver(set);
    if (bar instanceof HTMLElement) ro.observe(bar);
    if (satt instanceof HTMLElement) ro.observe(satt);
    window.addEventListener("resize", set);
    window.addEventListener("orientationchange", set);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", set);
      window.removeEventListener("orientationchange", set);
    };
  }, [session.ready]);

  useEffect(() => {
    document.documentElement.dataset["blackout"] = String(blackout);
    // Statuslinja på telefonen skal følge paletten, ikke stå igjen i den
    // andre. Verdien leses fra tokenet, så fargen finnes fortsatt ett sted.
    const meta = document.querySelector('meta[name="theme-color"]');
    const bg = getComputedStyle(document.documentElement)
      .getPropertyValue("--bg").trim();
    // Beholdes for Android og for skrivebordsnettlesere, som fortsatt leser den.
    if (meta !== null && bg.length > 0) meta.setAttribute("content", bg);

    /*
     * Dytt til statuslinja på iOS.
     *
     * Safari 26 ignorerer `theme-color` og maler statuslinja etter
     * bakgrunnsfargen til elementet øverst i visningsområdet. Den leser den
     * om igjen når det kommer et nytt lag der oppe, men ikke når en
     * CSS-variabel bytter verdi — så stripa ble stående i forrige paletts
     * farge til appen ble startet på nytt.
     *
     * Her legges det inn et element i den nye fargen øverst, som lever to
     * bilder og så fjernes. Det gir Safari lagendringen den trenger.
     * Elementet er én piksel høyt og ligger under statuslinja, så brukeren
     * ser det aldri. Lander avlesningen på elementet under i stedet, har
     * det samme farge, så utfallet er det samme.
     */
    if (bg.length === 0) return;
    const dytt = document.createElement("div");
    dytt.style.cssText = "position:fixed;top:0;left:0;right:0;height:1px;"
      + "pointer-events:none;z-index:2147483647;background:" + bg;
    document.body.appendChild(dytt);
    let indre = 0;
    const ytre = requestAnimationFrame(() => {
      indre = requestAnimationFrame(() => dytt.remove());
    });
    return () => {
      cancelAnimationFrame(ytre);
      cancelAnimationFrame(indre);
      dytt.remove();
    };
  }, [blackout]);

  /*
   * Ett målepunkt i minuttet er nok til å tegne driften over et skift.
   *
   * `now` skal IKKE i dep-lista. Den endrer seg hvert 100. millisekund, så
   * intervallet ble revet og satt opp på nytt før det rakk å løpe ut — og
   * målingen skjedde i praksis aldri. `sampleClock` leser klokka selv.
   */
  useEffect(() => {
    const id = window.setInterval(sampleClock, 60_000);
    return () => window.clearInterval(id);
  }, [sampleClock]);

  const docs = useMemo(() => {
    const ut = new Map<SideId, DocumentState>();
    /*
     * De faste sidene kjennes paa SLAG, feltene paa TITTEL.
     *
     * Det er ikke en inkonsekvens: alle egendefinerte har slaget `annet`, saa
     * to av dem ville truffet hverandre paa slag alene. Nettflata gjoer det
     * samme oppslaget naar den lager serien (`serieFor`), og de to maa vaere
     * enige om hva som er «det samme feltet».
     */
    const felter = egneFelter(dokumenter);
    const sider: readonly {
      readonly id: SideId;
      readonly navn: string;
      passer(slag: string, tittel: string): boolean;
    }[] = [
      ...DOKUMENTSIDER.map((p) => ({
        id: p as SideId,
        navn: sidenavn(p),
        /* Ogsaa et egendefinert felt som HETER det samme. Se `egneFelter`. */
        passer: (slag: string, tittel: string) =>
          slag === p || (slag === EGET_SLAG && sammeNavn(tittel, sidenavn(p))),
      })),
      ...felter.map((t) => ({
        id: feltId(t), navn: t,
        passer: (slag: string, tittel: string) => slag === EGET_SLAG && tittel === t,
      })),
    ];

    for (const side of sider) {
      const alle = dokumenter.filter((d) => side.passer(d.slag, d.tittel));
      const naa = alle.find((d) => d.opptaksdag === session.shootDay);
      /*
       * Tidligere dager sorteres paa opptaksdag, synkende.
       *
       * Dagen er en TEKST i modellen — den kan vaere «7», men ogsaa «7B»
       * eller «pilot». Et tall-sammenligning ville stokket dem feil, saa her
       * sammenlignes numerisk naar begge ER tall, og ellers paa tekst.
       */
      const tidligere = alle
        .filter((d) => d.opptaksdag !== session.shootDay)
        .sort((a, b) => {
          const x = Number(a.opptaksdag), y = Number(b.opptaksdag);
          if (Number.isFinite(x) && Number.isFinite(y)) return y - x;
          return b.opptaksdag.localeCompare(a.opptaksdag, "no");
        })
        .map((d) => ({
          serieId: d.serieId, opptaksdag: d.opptaksdag, dato: d.dato,
          versjon: d.versjon, url: d.url, ulest: d.ulest,
        }));

      ut.set(side.id, {
        page: side.id,
        title: side.navn,
        meta: naa === undefined ? null : metaAv(naa.versjon, naa.lastetOpp),
        stale: false,
        localUrl: naa?.url ?? null,
        /* Merket paa dagens dokument. «Ulest av deg» — se
           state/dokumenter.ts og migrering 21. */
        ulest: naa?.ulest ?? false,
        serieId: naa?.serieId ?? null,
        tidligere,
      });
    }
    return ut;
  }, [dokumenter, session.shootDay]);

  /*
   * ========  EN TOM FLATE VISES IKKE  ========
   *
   * Brukeren 02.11.2026: «Om man i appen har valgt aa se en flate, som
   * ikke har faatt informasjon i seg, saa skal ikke flaten vises. Slik at
   * man ikke ender opp med aa se masse flater man ikke faar noe ut av.»
   *
   * VALGET STAAR. Dette er ikke en avskruing — `pages` i oekten er
   * uroert, og lastes dagsmanus opp klokka fem, er sida der klokka fem.
   * Derfor en UTLEDET liste og ingen skriving: det finnes ingen tilstand
   * som kan komme i utakt med dokumentene.
   *
   * ARBEIDSFLATENE STAAR ALLTID. Logg, Hurtiglogg og Timer er tomme til
   * man selv skriver i dem, og de er stedet man skriver — en app som
   * skjulte loggen til noen hadde logget, ville ikke hatt noen vei inn.
   * Det er flatene som faar innhold ANDRE STEDER som kan vaere tomme:
   * dokumentene, briefen, shotlista, synkspoersmaalene.
   *
   * SU henger paa deltakerlista og ikke paa spoersmaalene. Er det folk
   * i produksjonen, kan regi skrive inn et spoersmaal selv der hen staar
   * — og da skal flata finnes.
   */
  const pages = useMemo(() => {
    const harNoe = (side: SideId): boolean => {
      switch (side) {
        case "logg": case "hurtiglogg": case "timer": return true;
        case "su": return suData.deltakere.length > 0 || suData.spoersmaal.length > 0;
        case "brief": return flater.some((f) => !erTomTekst(f.dok));
        case "shotlist": return shotliste.scener.length > 0 || shotliste.shots.length > 0;
        default: {
          const d = docs.get(side);
          /* Dagens dokument, eller en tidligere dag man kan bla til. */
          return d !== undefined && (d.localUrl !== null || d.tidligere.length > 0);
        }
      }
    };
    const igjen = valgteSider.filter(harNoe);
    /*
     * ALDRI EN BLANK APP.
     *
     * Har noen skrudd AV loggsidene og bare Dagsplan staar paa, kan
     * filteret toemme lista — og blankt ser ut som en feil, folk begynner
     * aa ringe. Se docs/12-ui-feltapp.md. Da staar den foerste valgte,
     * tom og med sitt eget navn i toppen, saa veien til menyen finnes.
     */
    return igjen.length > 0 ? igjen : valgteSider.slice(0, 1);
  }, [valgteSider, docs, flater, shotliste, suData]);

  /*
   * Indeksen kan ha pekt paa en side som nettopp ble tom.
   *
   * Pager og punktene teller paa `index` — uten denne sto appen paa en
   * side som ikke finnes lenger, med et punkt utenfor rada.
   */
  useEffect(() => {
    if (index >= pages.length && pages.length > 0) setIndex(pages.length - 1);
  }, [index, pages.length]);

  /**
   * Tidskoden står på alle flatene.
   *
   * Dette er endret fra dokumentene, som sier at den bare skal stå på Logg
   * og Hurtiglogg og at veggklokka står alene ellers. Eneste bryteren er
   * «Vis tidskode» i menyen, for produksjoner der klokkeslettet ikke skal
   * være synlig.
   */
  const showsTimecode = showTc;

  const liveFrames = useMemo(
    () => (anchor === null ? null : timecodeAt(anchor, now, spec).frames),
    [anchor, now, spec],
  );

  /**
   * Hva avlesningen viser, i prioritert rekkefølge: teksten som skrives
   * fryser tallet, så blaingen, ellers nåtid.
   */
  const readoutFrames = frozenFrames ?? scrollFrames ?? liveFrames;
  const live = frozenFrames === null && scrollFrames === null;

  const goto = useCallback((page: SideId): void => {
    const i = pages.indexOf(page);
    if (i >= 0) setIndex(i);
  }, [pages]);

  /*
   * Stabile prop-er til dokumentflatene.
   *
   * `doc` ble bygd paa nytt her for hver render, og `onOpen` var en ny pil
   * hver gang. Da gjengis flata uansett hva memo sier, og ref-ene paa kortene
   * kobles fra og paa igjen ti ganger i sekundet. Kartet regnes om bare naar
   * dokumentene faktisk endrer seg.
   */
  /*
   * ========  AA AAPNE ER AA LESE, OG DET BLIR HER  ========
   *
   * Merket fjernes naar dokumentet aapnes, ikke naar det hentes ned:
   * nedlastingen skjer i bakgrunnen uten at noen har sett noe.
   *
   * INGEN KVITTERING GAAR UT. Her sto en rad til `document_read`, slik
   * at koordinatoren kunne se hvem som hadde lest. Brukeren 02.11.2026:
   * «Vi trenger ikke aa overvaake hvem som har lest et dokument og
   * ikke. Dette er ikke nord-korea.»
   *
   * Merket klarer seg uten: det er TELEFONEN som vet hva den har vist
   * deg. Det virker ogsaa uten nett — som er naar man staar paa sett —
   * og ingenting om lesevanene dine forlater enheten.
   */
  const kvitter = useCallback((serieId: string | null): void => {
    if (serieId === null) return;
    void markerLest(serieId).then(() => {
      /* Lista leses om, saa merket forsvinner med én gang. */
      setDokumenter((f) => f.map(
        (d) => (d.serieId === serieId ? { ...d, ulest: false } : d)));
    });
  }, []);

  const openDoc = useCallback((page: SideId): void => {
    const d = docs.get(page);
    if (d === undefined) return;
    setVisning({ tittel: d.title, meta: d.meta ?? "ikke delt", url: d.localUrl });
    kvitter(d.serieId);
  }, [docs, kvitter]);

  const openTidligere = useCallback((side: SideId, t: TidligereDokument): void => {
    setVisning({
      tittel: sidenavn(side),
      meta: `Opptaksdag ${t.opptaksdag} · v${t.versjon}`,
      url: t.url,
    });
    kvitter(t.serieId);
  }, [kvitter]);

  const closeDoc = useCallback((): void => { setVisning(null); }, []);

  const screenFor = (page: SideId): JSX.Element => {
    switch (page) {
      case "logg":
        return (
          <LoggScreen
            key={page}
            merknader={merknader}
            deltakere={suData.deltakere}
            onNyttSpoersmaal={nyttSpoersmaal}
            editMode={editMode}
            onEditModeChange={setEditMode}
            onReadoutChange={setScrollFrames}
            onWritingChange={setFrozenFrames}
          />
        );
      case "hurtiglogg":
        return (
          <HurtigloggScreen key={page} editMode={editMode} onEditModeChange={setEditMode} />
        );
      case "su":
        return (
          <SuScreen
            key={page}
            deltakere={suData.deltakere}
            spoersmaal={suData.spoersmaal}
            stiltEllers={suData.stilt}
            onNyttSpoersmaal={nyttSpoersmaal}
          />
        );
      case "brief":
        return <BriefScreen key={page} flater={flater} onEndret={endreFlate} />;
      case "shotlist":
        return (
          <ShotlistScreen
            key={page}
            liste={shotliste}
            bildeklar={bildeklar}
            onHake={hukShot}
          />
        );
      case "timer":
        return <TimerScreen key={page} now={now} />;
      default: {
        const doc = docs.get(page);
        if (doc === undefined) return <div className="surface" />;
        return (
          <DocumentScreen
            key={page}
            doc={doc}
            page={page}
            onOpen={openDoc}
            onOpenTidligere={(t) => openTidligere(page, t)}
          />
        );
      }
    }
  };

  if (session.startupError !== null) {
    // Ikke en blank flate. Blankt ser ut som en feil, og folk begynner å
    // ringe. Se docs/12-ui-feltapp.md.
    return (
      <div className="app">
        <div className="startup">
          <span className="startup__title">Appen kan ikke starte</span>
          <span className="startup__body">{session.startupError}</span>
          <span className="startup__meta">{window.location.origin}</span>
          {session.startupErrorBypassable ? (
            <button
              type="button"
              className="startup__bypass"
              onClick={session.dismissStartupError}
            >
              Logg likevel — denne økta blir ikke lagret
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (!session.ready) {
    // Ingen inngangsanimasjon og ingen skimmer. Bare svart til ankeret finnes.
    return <div className="app" />;
  }

  return (
    <div className="app">
      <TopBar
        frames={showsTimecode ? readoutFrames : null}
        /*
         * TIDSKODEN ER PRODUKSJONENS, OG DEN ER JAMMENS.
         *
         * Appen gaar paa sin egen klokke innvendig saa poster kan
         * skrives foer noen har jammet — men den klokka er ikke en
         * tidskode noen har avtalt, og den skal ikke vises som om den
         * var det. Brukeren 01.11.2026: «Hvis den ikke er jammet i det
         * heletatt, saa skal den vises som streker.»
         */
        tcTilstand={showsTimecode
          ? jamInfo(session.anchor ?? null, now.wallUtcMs).tilstand : null}
        spec={spec}
        live={live}
        wallUtcMs={now.wallUtcMs}
        uncertainSince={uncertainSince}
        /*
         * «Privat» vises som alle andre produksjoner.
         *
         * Den sto foer uten forstavelse, og det var feil: er privat den
         * tilstanden som vises med blank linje, er den farlige tilstanden den
         * usynlige. Bytter man regelmessig, er det lett aa begynne aa logge
         * privat naar man skal skrive paa produksjonen.
         */
        productionName={session.production.name}
        produksjoner={velgerader}
        aktivProduksjonId={session.production.id}
        onVelgProduksjon={session.byttTilProduksjon}
        scrolledBack={scrollFrames !== null}
        editMode={editMode}
        /*
         * Bare én glassflate om gangen.
         *
         * To lag glass oppå hverandre samler bakgrunnen to ganger og leses
         * som tekstur i stedet for materiale — se liquid-glass-spec.md § 6.
         */
        onMenu={() => {
          if (editMode) { setEditMode(false); return; }
          /*
           * Knappen veksler. Den lukker gjennom menyens egen metode, slik at
           * utgangsanimasjonen beholdes.
           *
           * Leser menuShown og ikke menuOpen: menuOpen lever ut hele
           * utgangen, saa et trykk mens menyen gled ut lukket den en gang
           * til i stedet for aa aapne — mens ikonet alt viste hamburgeren.
           */
          if (menuShown) { menuRef.current?.close(); return; }
          setSheetOpen(false);
          /*
           * Er menyen fortsatt montert, er den paa vei ut. Da hentes den
           * tilbake — setMenuOpen(true) ville ikke gjort noe, siden den alt
           * er true, og tidsuret fra utgangen ville avmontert den etterpaa.
           */
          if (menuOpen) { menuRef.current?.reopen(); setMenuShown(true); return; }
          setMenuOpen(true);
          setMenuShown(true);
        }}
        menuOpen={menuShown}
        onTimecode={() => { setMenuOpen(false); setSheetOpen(true); }}
      />

      {/* Dimmer toppstripa når et overlegg er åpent. Se overlays.css. */}

      <Pager
        index={index}
        count={pages.length}
        onIndexChange={setIndex}
        /*
         * Sidebytte mens det skrives lagrer posten og legger vekk tastaturet.
         * Fokus ut er det som avslutter posten, saa det holder aa slippe
         * feltet — resten faller paa plass av seg selv.
         *
         * Kalles naar byttet er bestemt, ikke naar gesten starter: se Pager.
         */
        onPageCommit={() => {
          const aktiv = document.activeElement;
          if (aktiv instanceof HTMLElement) aktiv.blur();
        }}
      >
        {pages.map((page) => screenFor(page))}
      </Pager>

      {/*
        * Bunnstripa staar ogsaa mens det skrives.
        *
        * Den laa foer gjemt da — prikkene hadde ingen funksjon mens tastaturet
        * var oppe. Naa kan man bytte side uten aa gaa ut av tastaturet foerst,
        * og da er de baade opplysning og betjening.
        *
        * Den trenger ingen egen animasjon for aa komme opp: den ligger nederst
        * i flata, saa den foelger hoeyden som krymper — og havner dermed noeyaktig
        * mellom loggen og tastaturet.
        */}
        <div className="dots">
          {pages.map((page, i) => (
            <button
              key={page}
              type="button"
              className={`dots__dot${i === index ? " dots__dot--active" : ""}`}
              onClick={() => setIndex(i)}
              aria-label={`${sidenavn(page)}, side ${i + 1} av ${pages.length}`}
            />
          ))}
        </div>

      <Menu
        ref={menuRef}
        open={menuOpen}
        onClosing={() => setMenuShown(false)}
        onClose={() => setMenuOpen(false)}
        onAdjustTimecode={() => setSheetOpen(true)}
        onEditButtons={() => { goto("hurtiglogg"); setEditMode(true); }}
      />

      {/* Invitasjoner som nettopp bandt seg. Den ligger OVER alt, ogsaa
          menyen: beskjeden gjelder hvilken produksjon du staar i, og det
          er ikke et valg man tar bak noe annet. */}
      <Invitasjon paaVelg={session.byttTilProduksjon} />

      <TimecodeSheet open={sheetOpen} at={now} onClose={() => setSheetOpen(false)} />

      <DocumentViewer visning={visning} onClose={closeDoc} />
    </div>
  );
}

/**
 * Maksimert dokument.
 *
 * I liten tilstand er det ingen pinch, bare trykk. I maksimert tilstand er
 * det ingen sidewipe. Da finnes gestekonflikten mellom swipe og pinch-zoom
 * ikke. Ingen delingsknapp og ingen «åpne i …» — dokumentet forlater aldri
 * appen. Se docs/12-ui-feltapp.md.
 */
export interface Visning {
  readonly tittel: string;
  readonly meta: string;
  readonly url: string | null;
}

function DocumentViewer(
  { visning, onClose }: { visning: Visning | null; onClose(): void },
): JSX.Element | null {
  /*
   * Blir montert til utgangen er ferdig. Visningen holdes fast mens den gaar
   * ut, ellers ville tittelen tommet i samme bilde som bevegelsen startet.
   */
  const { mounted, shown } = useExitDelay(visning !== null, 250);
  const siste = useRef<Visning | null>(visning);
  if (visning !== null) siste.current = visning;
  const vist = siste.current;
  if (!mounted || vist === null) return null;

  return (
    <div className={`docfull${shown ? " docfull--in" : ""}`}>
      <DokumentFlate
        tittel={vist.tittel}
        meta={vist.meta}
        url={vist.url}
        onClose={onClose}
      />
    </div>
  );
}

/**
 * Innloggingsporten ligger UTENFOR SessionProvider.
 *
 * Er man ikke innlogget, skal den lokale loggen ikke engang aapnes — hvilken
 * produksjon den hoerer til vet vi foerst naar medlemskapet er hentet.
 *
 * Uten Supabase er porten alltid aapen, og appen kjoerer paa froeet. Da finnes
 * innloggingsskjermen ikke i det hele tatt.
 */
export default function App(): JSX.Element {
  const [tilstand, setTilstand] = useState<"sjekker" | "inne" | "ute">(
    krevInnlogging ? "sjekker" : "inne",
  );

  useEffect(() => {
    if (!krevInnlogging || supabase === null) return;
    let avbrutt = false;

    const avgjoer = async (): Promise<void> => {
      const inne = await innlogget();
      if (avbrutt) return;
      if (inne) await oppdaterOekt();
      if (avbrutt) return;
      setTilstand(inne ? "inne" : "ute");
    };
    void avgjoer();

    // Den magiske lenka lander i adressen og gir en oekt uten at vi spoer.
    const { data } = supabase.auth.onAuthStateChange(() => { void avgjoer(); });
    return () => { avbrutt = true; data.subscription.unsubscribe(); };
  }, []);

  // Ingen skimmer. Svart flate til svaret finnes, som ellers i appen.
  if (tilstand === "sjekker") return <div className="app" />;
  if (tilstand === "ute") return <InnloggingScreen />;

  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  );
}
